/**
 * A streaming SHA-256, written out rather than delegated to `crypto.subtle`.
 *
 * The architecture's media section describes verification as "streaming SHA-256
 * via `crypto.subtle.digest`", and that is not something `crypto.subtle` can
 * do: `digest()` takes one complete `BufferSource` and has no incremental form.
 * Attachments run to 1 GiB (`JOURNAL_ATTACHMENT_BYTE_LIMITS.video`), so the
 * whole-buffer route would have to hold the entire file in memory to hash it —
 * exactly what OPFS streaming was chosen to avoid. An incremental
 * implementation is therefore not a stylistic preference; it is the only shape
 * that satisfies F-JP-28's "streaming SHA-256 (1 MiB chunks)".
 *
 * It also has to be *here* rather than in an adapter. `@ff/store` may not reach
 * a browser API at all, and the digest is consumed by the import path, the
 * verifier and the repository's pre-write attachment check — all of which are
 * platform-free by contract.
 *
 * The algorithm is FIPS 180-4 §6.2. It is proven against the published vectors
 * by `describeDigestPort`, because an implementation that hashed *consistently*
 * but wrongly would pass every self-comparison in the app and still disagree
 * with every other SHA-256 in the world.
 */

import type { Sha256Hex } from "@ff/domain";
import { isSha256Hex } from "@ff/domain";
import type { DigestPort } from "@ff/domain/ports";

const ROUND_CONSTANTS = Uint32Array.from([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4,
  0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe,
  0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f,
  0x4a7484aa, 0x5cb0a9dc, 0x76f988da, 0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7,
  0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc,
  0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070, 0x19a4c116,
  0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7,
  0xc67178f2,
]);

const INITIAL_STATE = Uint32Array.from([
  0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c, 0x1f83d9ab,
  0x5be0cd19,
]);

const BLOCK_BYTES = 64;

function rotateRight(value: number, bits: number): number {
  return ((value >>> bits) | (value << (32 - bits))) >>> 0;
}

/**
 * Incremental SHA-256 state.
 *
 * One instance per digest and never shared: two attachments are imported at
 * once, and shared mutable state would interleave their blocks into a hash
 * belonging to neither file.
 */
export class MediaSha256Stream {
  readonly #state = Uint32Array.from(INITIAL_STATE);
  readonly #block = new Uint8Array(BLOCK_BYTES);
  readonly #schedule = new Uint32Array(64);
  #blockFilled = 0;
  #totalBytes = 0;
  #finished = false;

  update(bytes: Uint8Array): void {
    if (this.#finished) {
      throw new Error("MediaSha256Stream cannot be updated after digest()");
    }
    this.#totalBytes += bytes.byteLength;

    let offset = 0;
    // Top up a partially filled block first; only once the carry-over is gone
    // can whole blocks be consumed straight out of the caller's buffer without
    // a copy, which is what keeps a 1 GiB file from being copied twice.
    if (this.#blockFilled > 0) {
      const take = Math.min(BLOCK_BYTES - this.#blockFilled, bytes.byteLength);
      this.#block.set(bytes.subarray(0, take), this.#blockFilled);
      this.#blockFilled += take;
      offset = take;
      if (this.#blockFilled === BLOCK_BYTES) {
        this.#compress(this.#block, 0);
        this.#blockFilled = 0;
      }
    }

    while (bytes.byteLength - offset >= BLOCK_BYTES) {
      this.#compress(bytes, offset);
      offset += BLOCK_BYTES;
    }

    if (offset < bytes.byteLength) {
      this.#block.set(bytes.subarray(offset), 0);
      this.#blockFilled = bytes.byteLength - offset;
    }
  }

  /** Appends the padding and length, then renders 64 lowercase hex characters. */
  digest(): Sha256Hex {
    if (this.#finished) {
      throw new Error("MediaSha256Stream digest() is single-use");
    }
    this.#finished = true;

    const bitLength = this.#totalBytes * 8;
    const padding = new Uint8Array(this.#blockFilled < 56 ? 64 : 128);
    padding.set(this.#block.subarray(0, this.#blockFilled), 0);
    padding[this.#blockFilled] = 0x80;

    // The length is 64 bits; `Number` carries 53, so the high word is derived
    // by division rather than by a shift, which would have discarded it.
    const view = new DataView(padding.buffer);
    view.setUint32(padding.length - 8, Math.floor(bitLength / 0x1_0000_0000), false);
    view.setUint32(padding.length - 4, bitLength >>> 0, false);

    for (let offset = 0; offset < padding.length; offset += BLOCK_BYTES) {
      this.#compress(padding, offset);
    }

    let hex = "";
    for (const word of this.#state) hex += word.toString(16).padStart(8, "0");
    // `hex` is 64 lowercase hex characters by construction; the guard is the
    // only published way to obtain the brand, and a total function may not
    // depend on a construction argument holding.
    return isSha256Hex(hex) ? hex : UNREACHABLE_DIGEST;
  }

  #compress(source: Uint8Array, offset: number): void {
    const schedule = this.#schedule;
    for (let index = 0; index < 16; index += 1) {
      const at = offset + index * 4;
      schedule[index] =
        (((source[at] ?? 0) << 24) |
          ((source[at + 1] ?? 0) << 16) |
          ((source[at + 2] ?? 0) << 8) |
          (source[at + 3] ?? 0)) >>>
        0;
    }
    for (let index = 16; index < 64; index += 1) {
      const previous15 = schedule[index - 15] ?? 0;
      const previous2 = schedule[index - 2] ?? 0;
      const s0 =
        (rotateRight(previous15, 7) ^ rotateRight(previous15, 18) ^ (previous15 >>> 3)) >>>
        0;
      const s1 =
        (rotateRight(previous2, 17) ^ rotateRight(previous2, 19) ^ (previous2 >>> 10)) >>>
        0;
      schedule[index] =
        ((schedule[index - 16] ?? 0) + s0 + (schedule[index - 7] ?? 0) + s1) >>> 0;
    }

    let a = this.#state[0] ?? 0;
    let b = this.#state[1] ?? 0;
    let c = this.#state[2] ?? 0;
    let d = this.#state[3] ?? 0;
    let e = this.#state[4] ?? 0;
    let f = this.#state[5] ?? 0;
    let g = this.#state[6] ?? 0;
    let h = this.#state[7] ?? 0;

    for (let index = 0; index < 64; index += 1) {
      const sigma1 = (rotateRight(e, 6) ^ rotateRight(e, 11) ^ rotateRight(e, 25)) >>> 0;
      const choose = ((e & f) ^ (~e & g)) >>> 0;
      const temp1 =
        (h + sigma1 + choose + (ROUND_CONSTANTS[index] ?? 0) + (schedule[index] ?? 0)) >>>
        0;
      const sigma0 = (rotateRight(a, 2) ^ rotateRight(a, 13) ^ rotateRight(a, 22)) >>> 0;
      const majority = ((a & b) ^ (a & c) ^ (b & c)) >>> 0;
      const temp2 = (sigma0 + majority) >>> 0;

      h = g;
      g = f;
      f = e;
      e = (d + temp1) >>> 0;
      d = c;
      c = b;
      b = a;
      a = (temp1 + temp2) >>> 0;
    }

    this.#state[0] = ((this.#state[0] ?? 0) + a) >>> 0;
    this.#state[1] = ((this.#state[1] ?? 0) + b) >>> 0;
    this.#state[2] = ((this.#state[2] ?? 0) + c) >>> 0;
    this.#state[3] = ((this.#state[3] ?? 0) + d) >>> 0;
    this.#state[4] = ((this.#state[4] ?? 0) + e) >>> 0;
    this.#state[5] = ((this.#state[5] ?? 0) + f) >>> 0;
    this.#state[6] = ((this.#state[6] ?? 0) + g) >>> 0;
    this.#state[7] = ((this.#state[7] ?? 0) + h) >>> 0;
  }
}

const UNREACHABLE_DIGEST = "0".repeat(64) as Sha256Hex;

/**
 * The {@link DigestPort} implementation the import and verify paths use.
 *
 * Stateless: every call builds its own {@link MediaSha256Stream}, so the port
 * is safe to share across concurrent imports without any coordination.
 */
export function createMediaDigest(): DigestPort {
  return {
    async sha256(chunks: AsyncIterable<Uint8Array>): Promise<Sha256Hex> {
      const stream = new MediaSha256Stream();
      for await (const chunk of chunks) stream.update(chunk);
      return stream.digest();
    },
  };
}
